import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import { updateEntity, withEntities } from '@ngrx/signals/entities';
import {
  fetchAllPages,
  withCursorPagination,
  withPickerList,
} from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { CellularMintPlanService } from './generated/cellular-mint-plan/cellular-mint-plan.service';
// Self-contained lib: the store speaks Orval-GENERATED types, NOT
// `@dgacontrol-frontend/core`. The app components bridge generated↔core.
import type {
  CellularV2MintPlansListParams,
  MintPlanCreateRequestV2Request,
  MintPlanV2,
  PatchedMintPlanPatchRequestV2Request,
} from './generated/types';

/**
 * TARG-4 puro: inject the Orval `CellularMintPlanService` directly, no facade
 * layer. The generated response shape matches 1:1 what the store needs. Precedent:
 * `ConfigurationStore` (feat-system-configurations-crud, 2026-08-28).
 */
export const MintPlansStore = signalStore(
  withEntities<MintPlanV2>(),
  // Table: v2 cursor list. COMPONENT-DRIVEN (dt-mint-plans calls load({}) on init).
  // The frm-simcard picker consumes `allMintPlans` (dropdown slice below), not this.
  withCursorPagination<MintPlanV2, CellularV2MintPlansListParams>(() => {
    const api = inject(CellularMintPlanService);
    // The generated page already is { results, next, previous }: no adapter.
    return (query, cursor) =>
      api.cellularV2MintPlansList({ ...query, cursor: cursor ?? undefined });
  }),
  // Picker list (the SIM form's plan dropdown), SEPARATE from the partial cursor
  // entities() (store-conventions §"Tabla cursor + dropdowns").
  withPickerList('mintPlans', () => {
    const api = inject(CellularMintPlanService);
    return () =>
      fetchAllPages((cursor) => api.cellularV2MintPlansList({ page_size: 1000, cursor }));
  }),
  withMethods((store, api = inject(CellularMintPlanService)) => ({
    // POST → single `{mint_plan}`. Do NOT addEntity into a cursor list: the new
    // row's server-side sort position is unknown → inserting locally desyncs
    // the cursor → dup/missing rows on the next loadMore. Reset to page 1 via
    // the cursor feature's `load` (server-authoritative).
    createMintPlan(body: MintPlanCreateRequestV2Request) {
      return api.cellularV2MintPlansCreate(body).pipe(
        tap(() => {
          store.load({});
        }),
        map((res) => res.mint_plan),
      );
    },
    // PATCH → single `{mint_plan}` → patch the row IN PLACE. idKey = default
    // `id` (the withEntities selectId). The URL path param `identity` is only
    // needed for routing; the cursor stays valid (no reorder).
    updateMintPlan(
      body: PatchedMintPlanPatchRequestV2Request,
      identity: string,
    ) {
      return api.cellularV2MintPlansPartialUpdate({ identity }, body).pipe(
        tap((res) => {
          patchState(
            store,
            updateEntity({ id: res.mint_plan.id, changes: res.mint_plan }),
          );
        }),
        map((res) => res.mint_plan),
      );
    },
  })),
);
